import moment from "moment";
import React, { useEffect, useState } from "react";
import { Alert, Card, Form, Table } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link, useParams, useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { GameKeySource, GameRelationshipKind, GameType } from "@/const";
import { useRequest } from "@components/AppHook";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { Awesome, DevModal } from "@components/Helpers";
import { ModalDelete } from "@components/Modals";
import { CommonCard } from "@components/Pages";
import { calcDiscountFromPrice, calcPriceFromDiscount, url_reverse } from "@components/Utils";

export function Prices(props) {
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    if (gameCommonInfo.game.is_steam_gift) {
        return <SteamGiftPrices {...props} />;
    }
    return <StdPrices {...props} />;
}

function SteamGiftPrices({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData, apiIsLoading } = useAppApi(
        url_reverse("api:game:steam_gift_prices", { game_id: gameId })
    );
    const onFormSubmit = useSubmit();

    if (!apiData) return;

    const fieldProps = { apiData, setApiData, apiIsLoading, hideLabel: true, asRow: false };

    return (
        <CommonCard header={header}>
            <Form onSubmit={onFormSubmit}>
                <Table hover responsive className="dmg-table">
                    <thead>
                        <tr>
                            <th>{t("Регион")}</th>
                            <th>{t("Цена")}</th>
                            <th>{t("Период скидки")}</th>
                            <th>{t("Цена со скидкой")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(apiData.forms_data).map((region) => {
                            return (
                                <tr key={region}>
                                    <td>{region}</td>
                                    <td>
                                        <DevForm.Field {...fieldProps} prefix={region} name="price" />
                                    </td>
                                    <td>
                                        <DevForm.Field {...fieldProps} prefix={region} name="discount_period" />
                                    </td>
                                    <td>
                                        <DevForm.Field {...fieldProps} prefix={region} name="discount_price" />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
                <DevForm.Button variant="primary" type="submit" name="save">
                    {t("Сохранить")}
                </DevForm.Button>
            </Form>
        </CommonCard>
    );
}

function StdPrices({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppApi(url_reverse("api:game:prices", { game_id: gameId }));
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const [cbDisablePrices, setCbDisablePrices] = useState(false);
    const onFormSubmit = useSubmit();
    const [futureCampModal, setFutureCampModal] = useState(false);
    const { setDelData } = useAppContext();
    const { api_post } = useRequest();

    useEffect(() => {
        if (apiData?.forms_data && gameCommonInfo.game.bundle) {
            if (gameCommonInfo.game.bundle?.kind === GameRelationshipKind.BUNDLE_ADD_COLLECTION) {
                setCbDisablePrices(true);
            } else {
                const { data } = apiData.forms_data.main;
                const all_prices = parseFloat(data.price_rub) + parseFloat(data.price_eur) + parseFloat(data.price_usd);
                setCbDisablePrices(!(all_prices > 0));
            }
        }
    }, [apiData?.status]);

    useEffect(() => {
        if (apiData?.forms_data && gameCommonInfo.game.bundle) {
            if (gameCommonInfo.game.bundle?.kind === GameRelationshipKind.BUNDLE_ADD_COLLECTION) {
                setCbDisablePrices(true);
            } else {
                const { data } = apiData.forms_data.main;
                const all_prices = parseFloat(data.price_rub) + parseFloat(data.price_eur) + parseFloat(data.price_usd);
                setCbDisablePrices(!(all_prices > 0));
            }
        }
    }, []);

    useEffect(() => {
        if (apiData?.forms_data) {
            if (apiData.forms_data.price_rub) {
                const newApiData = { ...apiData };
                if (cbDisablePrices) {
                    newApiData.forms_data.main.data.price_rub = "0";
                    newApiData.forms_data.main.data.price_eur = "0";
                    newApiData.forms_data.main.data.price_usd = "0";
                    newApiData.forms_data.main.fields.price_rub.disabled = true;
                    newApiData.forms_data.main.fields.price_eur.disabled = true;
                    newApiData.forms_data.main.fields.price_usd.disabled = true;
                } else {
                    newApiData.forms_data.main.fields.price_rub.disabled = false;
                    newApiData.forms_data.main.fields.price_eur.disabled = false;
                    newApiData.forms_data.main.fields.price_usd.disabled = false;
                }
                setApiData(newApiData);
            }
        }
    }, [cbDisablePrices]);

    useEffect(() => {
        const newApiData = { ...apiData };
        if (apiData?.forms_data) {
            newApiData.forms_data.main.fields.price_rub.disabled = Boolean(
                apiData.forms_data.main.data.is_automatic_pricing
            );
            newApiData.forms_data.main.fields.price_eur.disabled = Boolean(
                apiData.forms_data.main.data.is_automatic_pricing
            );
            newApiData.forms_data.main.fields.price_usd.disabled = Boolean(
                apiData.forms_data.main.data.is_automatic_pricing
            );
        }
        setApiData(newApiData);
    }, [JSON.stringify(apiData?.forms_data)]);

    if (!apiData) return;

    function switchAutomaticPricing() {
        api_post(null, apiUrl, {
            params: { action: "switch_automatic_pricing" },
            then: (resp) => {
                if (resp.status === "ok") {
                    const newApiData = { ...apiData };
                    Object.keys(resp).forEach((k) => {
                        if (k in newApiData.forms_data.main.data) {
                            newApiData.forms_data.main.data[k] = resp[k];
                        }
                    });
                    setApiData(newApiData);
                }
            },
        });
    }

    const FutureCampConfirmModal = () => (
        <DevModal
            size="lg"
            show={futureCampModal}
            onHide={() => setFutureCampModal(false)}
            header={t("Подтвердите своё действие")}
            body={
                <Trans>
                    Вы изменяете основную цену игры, однако на данный момент есть запланированные акции, в которых
                    указана скидка, рассчитанная от старой цены игры. Пересчитать цену со скидкой по всем
                    запланированным акциям, используя новую цену и заданный процент скидки?
                </Trans>
            }
            footer={
                <>
                    <DevForm.Button variant="secondary" name="cancel" onClick={() => setFutureCampModal(false)}>
                        {t("Отмена")}
                    </DevForm.Button>
                    <DevForm.Button
                        variant="primary"
                        type="submit"
                        name="save"
                        onClick={(ev) => {
                            setFutureCampModal(false);
                            onFormSubmit(ev);
                        }}
                    >
                        {t("Сохранить")}
                    </DevForm.Button>
                </>
            }
        />
    );

    const now = moment();
    const canAddCampaign =
        gameCommonInfo.game.type !== GameType.CODE ||
        (gameCommonInfo.game.type === GameType.CODE && gameCommonInfo.game.key_source === GameKeySource.DEVAREA);

    if (apiData.changing_key_source) {
        return (
            <Alert variant="info">
                <Trans>
                    Переключение источника ключей началось и займет 10–15 минут. В это время продажи и обработка
                    платежей временно приостановлены. Процесс завершится автоматически, и продажи возобновятся.
                </Trans>
            </Alert>
        );
    }

    return (
        <>
            <CommonCard header={header}>
                {cbDisablePrices && (
                    <div className="dmg-callout dmg-callout-info mb-3">
                        {t("Цена набора формируется автоматически, путем суммирования цен ее продуктов")}
                    </div>
                )}
                {!cbDisablePrices && gameCommonInfo.game.is_sellable && apiData.forms_data.main.data.price_rub <= 0 && (
                    <div className="dmg-callout dmg-callout-danger mb-3">
                        <Trans>
                            Покупка недоступна, пока не указаны цены. Заполните все ценовые поля и нажмите «Сохранить».
                        </Trans>
                    </div>
                )}
                {gameCommonInfo.game.bundle?.kind === GameRelationshipKind.BUNDLE_FULL_PRICE && (
                    <DevForm.Field prefix="main" name="is_automatic_pricing" onChange={switchAutomaticPricing} />
                )}
                {gameCommonInfo.game.is_sellable && (
                    <Form onSubmit={onFormSubmit}>
                        {gameCommonInfo.game.type === GameType.CODE && (
                            <>
                                <div className="dmg-callout dmg-callout-info mb-3">
                                    {gameCommonInfo.game.key_source === GameKeySource.DEVAREA ? (
                                        <>
                                            <Trans>
                                                Выбранный режим позволяет вам самостоятельно управлять ключами
                                                активации, ценами и акциями на игру. Вы можете закупать ключи оптом по
                                                выгодной цене, устанавливать свои ценовые параметры и проводить
                                                собственные акции для привлечения покупателей.
                                            </Trans>
                                            <br />
                                            <Trans>
                                                Все настройки цен и акций будут доступны для редактирования вручную.
                                                Используйте этот режим, если вам требуется гибкость в настройке и
                                                индивидуальный подход к продажам.
                                            </Trans>
                                        </>
                                    ) : (
                                        <>
                                            <Trans>
                                                В этом режиме все данные по ключам активации, рекомендованным ценам и
                                                акциям будут автоматически загружаться от поставщика. Это позволяет вам
                                                упростить процесс продаж, обеспечивая актуальность информации о ценах и
                                                акциях без необходимости ручного управления.
                                            </Trans>
                                            <br />
                                            <Trans>
                                                Все настройки цен и акций управляются поставщиком и обновляются
                                                автоматически, что делает этот режим идеальным для быстрого и удобного
                                                запуска продаж.
                                            </Trans>
                                        </>
                                    )}
                                </div>
                                <DevForm.Field prefix="main" name="key_source_custom" />
                            </>
                        )}
                        {gameCommonInfo.game.bundle?.kind !== GameRelationshipKind.BUNDLE_ADD_COLLECTION && (
                            <>
                                <DevForm.Field prefix="main" name="price_rub" />
                                <DevForm.Field prefix="main" name="price_eur" />
                                <DevForm.Field prefix="main" name="price_usd" />
                            </>
                        )}
                        <DevForm.Field prefix="main" name="non_refundable" />
                        {apiData.has_future_campaigns ? (
                            <>
                                <DevForm.Button
                                    variant="primary"
                                    name="show_future_camp_modal"
                                    onClick={() => setFutureCampModal(true)}
                                >
                                    {t("Сохранить")}
                                </DevForm.Button>
                                {FutureCampConfirmModal()}
                            </>
                        ) : (
                            gameCommonInfo.game.bundle?.kind !== GameRelationshipKind.BUNDLE_ADD_COLLECTION && (
                                <DevForm.Button variant="primary" type="submit" name="save">
                                    {t("Сохранить")}
                                </DevForm.Button>
                            )
                        )}
                    </Form>
                )}
            </CommonCard>

            {gameCommonInfo.game.is_sellable && (
                <Card className="mt-4">
                    <Card.Header>
                        {t("Скидки")}
                        {canAddCampaign && (
                            <div className="dmg-card-header-tools">
                                <Link
                                    to={url_reverse("games:campaign:edit", { game_id: gameId, campaign_id: "add" })}
                                    className="btn btn-sm btn-dark"
                                >
                                    <Awesome type="plus-circle" /> {t("Добавить")}
                                </Link>
                            </div>
                        )}
                    </Card.Header>
                    <Card.Body>
                        <ModalDelete />
                        <Table hover responsive className="dmg-table">
                            <thead>
                                <tr>
                                    <th>{t("Кампания")}</th>
                                    <th>{t("Дата начала")}</th>
                                    <th>{t("Дата окончания")}</th>
                                    <th>{t("Скидка RUB, %")}</th>
                                    <th>{t("Скидка USD, %")}</th>
                                    <th>{t("Скидка EUR, %")}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {apiData.object_list?.map((o, i) => {
                                    return (
                                        <tr key={`object_list${i}`}>
                                            <td>
                                                <Link
                                                    to={url_reverse("games:campaign:edit", {
                                                        game_id: gameId,
                                                        campaign_id: o.id,
                                                    })}
                                                >
                                                    {o.ext_camp_id > 0 && <>[{o.ext_camp_id}]</>} {o.name}
                                                </Link>
                                            </td>
                                            <td>{o.date_start}</td>
                                            <td>{o.date_end}</td>
                                            <td>{o.discount_rub}</td>
                                            <td>{o.discount_usd}</td>
                                            <td>{o.discount_eur}</td>
                                            <td className="dmg-table-row-tools">
                                                {o.source === "manual" && moment(o.date_end).isAfter(now) && (
                                                    <Awesome
                                                        type="trash-o"
                                                        color="red"
                                                        onClick={() =>
                                                            setDelData({ url: apiUrl + "?campaign_id=" + o.id })
                                                        }
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}
        </>
    );
}

export function Campaigns({ gameId, header }) {
    const params = useParams();
    const { apiData, setApiData } = useAppApi(
        url_reverse("api:game:campaign:edit", { game_id: gameId, campaign_id: params.id })
    );
    const { t } = useTranslation();
    const commonInfo = useRouteLoaderData("commonInfo");
    const onFormSubmit = useSubmit();
    const [codeAlert, setCodeAlert] = useState(false);
    const game = apiData?.game;

    useEffect(() => {
        if (apiData?.forms_data.main.data && apiData?.curr_code_price) {
            setCodeAlert(apiData.forms_data.main.data.discount_price_rub < parseFloat(apiData.curr_code_price));
        }
    }, [apiData?.forms_data.main.data.discount_price_rub]);

    if (!apiData) return;

    const changeDiscount = (ev) => {
        const { name, val } = ev;
        const newApiData = { ...apiData };
        newApiData.forms_data.main.data[name] = val;
        ["rub", "usd", "eur"].forEach((curr) => {
            if (name === `discount_${curr}`) {
                newApiData.forms_data.main.data[`discount_price_${curr}`] =
                    newApiData.forms_data.main.data[name] === ""
                        ? ""
                        : calcPriceFromDiscount(curr, game[`price_${curr}`], newApiData.forms_data.main.data[name]);
            }
        });
        setApiData(newApiData);
    };

    const changePrice = (ev) => {
        const { name, val } = ev;
        const newApiData = { ...apiData };
        newApiData.forms_data.main.data[name] = val;
        if (name === "discount_price_rub") {
            newApiData.forms_data.main.data.discount_rub = calcDiscountFromPrice(game.price_rub, Math.floor(val));
        }
        if (name === "discount_price_usd") {
            newApiData.forms_data.main.data.discount_usd = calcDiscountFromPrice(game.price_usd, val);
        }
        if (name === "discount_price_eur") {
            newApiData.forms_data.main.data.discount_eur = calcDiscountFromPrice(game.price_eur, val);
        }
        setApiData(newApiData);
    };

    const fieldProps = { apiData, setApiData };
    return (
        <CommonCard header={header}>
            {codeAlert && <Alert variant="danger">{t("Внимание! Цена со скидкой ниже закупочной.")}</Alert>}
            <Form onSubmit={(ev) => onFormSubmit(ev, { backUrl: url_reverse("games:prices", { game_id: game.id }) })}>
                <DevForm.Field {...fieldProps} prefix="main" name="name" />
                <DevForm.Field {...fieldProps} prefix="main" name="date_start" />
                <DevForm.Field {...fieldProps} prefix="main" name="date_end" />
                <DevForm.Field {...fieldProps} prefix="main" name="discount_rub" onChange={changeDiscount} />
                <DevForm.Field {...fieldProps} prefix="main" name="discount_price_rub" onChange={changePrice} />
                <DevForm.Field {...fieldProps} prefix="main" name="discount_usd" onChange={changeDiscount} />
                <DevForm.Field {...fieldProps} prefix="main" name="discount_price_usd" onChange={changePrice} />
                <DevForm.Field {...fieldProps} prefix="main" name="discount_eur" onChange={changeDiscount} />
                <DevForm.Field {...fieldProps} prefix="main" name="discount_price_eur" onChange={changePrice} />
                <DevForm.Button
                    variant="primary"
                    type="submit"
                    name="save"
                    disabled={apiData?.campaign?.disable_editing && !commonInfo.account.is_super}
                >
                    {t("Сохранить")}
                </DevForm.Button>
            </Form>
        </CommonCard>
    );
}
